import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Truck, ShoppingCart, Building, PackageSearch, Store, Users, Briefcase, Heart, Utensils, GraduationCap, ArrowUpRight } from "lucide-react";
import { RevealOnScroll, StaggerContainer, StaggerItem } from "../effects/AnimatedElements";
import { useLanguage } from "@/context/LanguageContext";

const ICON_MAP = { Truck, ShoppingCart, Building, PackageSearch, Store, Users, Briefcase, Heart, Utensils, GraduationCap };

const defaultContent = {
  section_label: "Experiencia Multisectorial",
  title: "Sectores que Transformamos",
  subtitle: "Desarrollamos soluciones especializadas para cada industria, adaptándonos a las necesidades únicas de cada sector.",
  sectors: [
    { id: "logistica", icon: "Truck", title: "Logística y Envíos", description: "Gestión completa de flotas, rutas y entregas.", image: "https://images.unsplash.com/photo-1574471590229-658c1991b543?w=800" },
    { id: "comercio", icon: "ShoppingCart", title: "Comercio Electrónico", description: "Plataformas de e-commerce robustas.", image: "https://images.unsplash.com/photo-1656275544060-1c0515d192b6?w=800" },
    { id: "finanzas", icon: "Building", title: "Servicios Financieros", description: "Sistemas de contabilidad y facturación.", image: "https://images.unsplash.com/photo-1770520212379-233b02a9e21f?w=800" },
    { id: "inventarios", icon: "PackageSearch", title: "Gestión de Inventarios", description: "Control total de stock y almacenes.", image: "https://images.unsplash.com/photo-1761063443599-4c0edee38e61?w=800" },
    { id: "punto-venta", icon: "Store", title: "Puntos de Venta", description: "Sistemas POS modernos.", image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800" },
    { id: "recursos-humanos", icon: "Users", title: "Recursos Humanos", description: "Gestión de personal y nóminas.", image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800" }
  ]
};

const SectorsSection = ({ content }) => {
  const data = { ...defaultContent, ...content };
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const { t } = useLanguage();

  return (
    <section
      id="sectores"
      ref={sectionRef}
      data-testid="sectors-section"
      className="py-24 lg:py-32 relative overflow-hidden"
    >
      {/* Animated background dots */}
      <div className="absolute inset-0">
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-[#3b82f6] rounded-full"
            style={{
              left: `${20 + i * 15}%`,
              top: `${15 + i * 12}%`,
            }}
            animate={{
              opacity: [0.2, 0.6, 0.2],
              scale: [1, 1.5, 1],
            }}
            transition={{ duration: 3 + i, repeat: Infinity, delay: i * 0.5 }}
          />
        ))}
      </div>

      <div className="section-container relative z-10">
        {/* Header */}
        <RevealOnScroll direction="up" className="text-center mb-16">
          <motion.span
            className="text-[#3b82f6] text-sm font-semibold tracking-wider uppercase mb-4 inline-flex items-center gap-3"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
          >
            <motion.span
              className="w-10 h-px bg-gradient-to-r from-transparent to-[#3b82f6]"
              initial={{ scaleX: 0 }}
              animate={isInView ? { scaleX: 1 } : {}}
              transition={{ delay: 0.3 }}
            />
            {t("sectors_label")}
            <motion.span
              className="w-10 h-px bg-gradient-to-l from-transparent to-[#3b82f6]"
              initial={{ scaleX: 0 }}
              animate={isInView ? { scaleX: 1 } : {}}
              transition={{ delay: 0.3 }}
            />
          </motion.span>
          <h2
            data-testid="sectors-title"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6"
          >
            {t("sectors_title")}
          </h2>
          <p className="text-[#94a3b8] text-lg max-w-2xl mx-auto">
            {t("sectors_subtitle")}
          </p>
          <p className="text-[#3b82f6]/50 text-sm font-semibold tracking-widest uppercase mt-3">
            Global Metafora APP
          </p>
        </RevealOnScroll>

        {/* Sector cards */}
        <StaggerContainer staggerDelay={0.1} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.sectors.map((sector, index) => {
            const IconComponent = ICON_MAP[sector.icon] || Building;
            return (
              <StaggerItem key={sector.id}>
                <motion.div
                  data-testid={`sector-card-${sector.id}`}
                  className="sector-card group cursor-pointer relative"
                  whileHover={{ y: -8 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Image with zoom on hover */}
                  <motion.div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url('${sector.image}')` }}
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.6 }}
                  />
                  
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#02040a] via-[#02040a]/70 to-[#02040a]/30 group-hover:via-[#02040a]/50 group-hover:to-[#02040a]/10 transition-all duration-500" />

                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <motion.div
                          className="w-10 h-10 bg-[#3b82f6]/20 backdrop-blur-sm flex items-center justify-center"
                          whileHover={{ scale: 1.1, backgroundColor: "rgba(59,130,246,0.3)" }}
                        >
                          <IconComponent className="w-5 h-5 text-[#3b82f6]" />
                        </motion.div>
                        <h3 className="text-lg font-bold text-white">{sector.title}</h3>
                      </div>
                      <motion.div
                        className="w-8 h-8 flex items-center justify-center text-[#3b82f6] opacity-0 group-hover:opacity-100"
                        initial={{ x: -10, opacity: 0 }}
                        whileHover={{ scale: 1.2 }}
                        transition={{ duration: 0.3 }}
                      >
                        <ArrowUpRight className="w-5 h-5" />
                      </motion.div>
                    </div>
                    <motion.p
                      className="text-[#94a3b8] text-sm"
                    >
                      {sector.description}
                    </motion.p>
                  </div>

                  {/* Border glow on hover */}
                  <motion.div
                    className="absolute inset-0 border-2 border-transparent group-hover:border-[#3b82f6]/40 z-20 pointer-events-none"
                    transition={{ duration: 0.3 }}
                  />

                  {/* Number indicator */}
                  <motion.span
                    className="absolute top-4 right-4 text-white/10 text-5xl font-bold z-10 group-hover:text-white/20 transition-colors duration-300"
                  >
                    {String(index + 1).padStart(2, "0")}
                  </motion.span>
                </motion.div>
              </StaggerItem>
            );
          })}
        </StaggerContainer>
      </div>
    </section>
  );
};

export default SectorsSection;
